import { Alert, Button, Card, FormButtonRow, FormGroup, PageHeader } from '@ksyos/design-system';
import { useState } from 'react';
import { ManagerAuthorization, ToastType, ValidationErrorCode } from '@common/enums';
import { FileInput } from '@frontend/components/elements/FileInput';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import { showToast } from '@frontend/utils/showToast';

export function ImportOtpDevices() {
    const [fileName, setFileName] = useState<string | null>(null);
    const [fileData, setFileData] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    assertHasAuthorization(ManagerAuthorization.importHardwareTokens);

    return (
        <PageTemplate width="medium">
            <PageHeader title={translateKey('imports.import')} />

            {errorMessage && <Alert appearance="warning">{errorMessage}</Alert>}

            <Card>
                <Card.Header title={translateKey('imports.authenticationDevices')} />
                <Card.Body>
                    <FormGroup label={translateKey('imports.pkscFile')}>
                        <FileInput
                            extension=".xml"
                            fileName={fileName}
                            onChange={async (result) => {
                                setFileName(result.fileName);
                                setFileData(result.data);
                            }}
                            onError={async (message) => setErrorMessage(message)}
                        />
                    </FormGroup>
                    <FormButtonRow>
                        <Button
                            isDisabled={isLoading || !fileData}
                            onClick={async () => await uploadFile()}
                        >
                            {translateKey('upload.upload')}
                        </Button>
                    </FormButtonRow>
                </Card.Body>
            </Card>
        </PageTemplate>
    );

    async function uploadFile() {
        if (fileData && fileName) {
            setIsLoading(true);
            setErrorMessage(undefined);

            const item = {
                data: fileData,
            };

            const result = await fetchJson('/api/otpDevices/import', 'POST', item, [
                ValidationErrorCode.BAD_XML_DOCUMENT,
                ValidationErrorCode.INVALID_XML_DATA,
            ]);

            if (result.errorCode === ValidationErrorCode.BAD_XML_DOCUMENT) {
                await setErrorMessage(translateKey('imports.invalidDataMessage', fileName));
            } else if (result.errorCode === ValidationErrorCode.INVALID_XML_DATA) {
                await setErrorMessage(translateKey('imports.corruptedDataMessage', fileName));
            }

            if (result.status === 200) {
                setFileName(null);
                setFileData(null);

                showToast(
                    ToastType.success,
                    `${result.json.numberOfRegisteredDevices ?? 0} ${translateKey(
                        'imports.importDevicesSuccessMessage'
                    )}`
                );
            }
            setIsLoading(false);
        }
    }
}
